import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {Progress, Text} from '@gravity-ui/uikit';
import {showPriceLogo} from 'settings';
import {Ban, Star, StarFill} from '@gravity-ui/icons';
import {AppContext} from '@app/context';
import {usePriceListPreferences} from '@features/profile';
import {useMenuKeyboard} from '@hooks/keyboard';

import './Rating.scss';

const b = block('rating');

export interface RatingProps {
    value: number;
    className: string;
    priceList?: string;
    favorite?: boolean;
    banned?: boolean;
    enablePriceListActions?: boolean;
}

export const Rating: React.FC<RatingProps> = ({
    className,
    value,
    priceList,
    favorite = false,
    banned = false,
    enablePriceListActions = false
}) => {
    const {
        state: {clientDetails}
    } = useContext(AppContext);
    const login = clientDetails?.login;
    const {addPreference, removePreference, findPreferenceIdByPriceList} =
        usePriceListPreferences(login);

    const [isOpen, setIsOpen] = useState(false);
    const [actionPending, setActionPending] = useState(false);
    const [optimistic, setOptimistic] = useState<{favorite: boolean; banned: boolean} | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setOptimistic(null);
    }, [favorite, banned]);

    const isFavorite = optimistic?.favorite ?? favorite;
    const isBanned = optimistic?.banned ?? banned;

    const priceListActionsEnabled = enablePriceListActions;

    const isPopoverDisabled = !(showPriceLogo && priceList);
    const canUsePreferences = Boolean(login) && priceListActionsEnabled && !actionPending;

    const rank = (value: number): string => {
        if (!value) {
            return '—';
        } else return `${value} %`;
    };

    const progressValue = value === 0 ? NaN : value;
    const progressText = rank(value);

    const handleToggle = () => {
        if (isPopoverDisabled) {
            return;
        }

        setIsOpen((prev) => !prev);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (isPopoverDisabled) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((prev) => !prev);
        }

        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isPopoverDisabled) {
            setIsOpen(false);
        }
    }, [isPopoverDisabled]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;

            if (!target || !rootRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const revertOptimistic = useCallback(() => {
        setOptimistic(null);
    }, []);

    const runPreferenceMutation = useCallback(
        async (fn: () => Promise<void>) => {
            if (!priceList || !canUsePreferences) {
                return;
            }

            setActionPending(true);

            try {
                await fn();
            } catch (error) {
                console.error('Rating priceList preference error', error);
                revertOptimistic();
            } finally {
                setActionPending(false);
                setIsOpen(false);
            }
        },
        [priceList, canUsePreferences, revertOptimistic]
    );

    const handleAddFavorite = useCallback(() => {
        setOptimistic({favorite: true, banned: false});
        void runPreferenceMutation(async () => {
            await addPreference({kind: 'favorite', value: priceList!, comment: ''});
        });
    }, [addPreference, priceList, runPreferenceMutation]);

    const handleAddBan = useCallback(() => {
        setOptimistic({favorite: false, banned: true});
        void runPreferenceMutation(async () => {
            await addPreference({kind: 'ban', value: priceList!, comment: ''});
        });
    }, [addPreference, priceList, runPreferenceMutation]);

    const handleRemoveFavorite = useCallback(() => {
        setOptimistic({favorite: false, banned: false});
        void runPreferenceMutation(async () => {
            const id = await findPreferenceIdByPriceList('favorite', priceList!);
            if (id) {
                await removePreference(id);
            }
        });
    }, [findPreferenceIdByPriceList, removePreference, priceList, runPreferenceMutation]);

    const handleRemoveBan = useCallback(() => {
        setOptimistic({favorite: false, banned: false});
        void runPreferenceMutation(async () => {
            const id = await findPreferenceIdByPriceList('ban', priceList!);
            if (id) {
                await removePreference(id);
            }
        });
    }, [findPreferenceIdByPriceList, removePreference, priceList, runPreferenceMutation]);

    const handleSwitchToFavorite = useCallback(() => {
        setOptimistic({favorite: true, banned: false});
        void runPreferenceMutation(async () => {
            const banId = await findPreferenceIdByPriceList('ban', priceList!);
            if (banId) {
                await removePreference(banId);
            }
            await addPreference({kind: 'favorite', value: priceList!, comment: ''});
        });
    }, [
        addPreference,
        findPreferenceIdByPriceList,
        removePreference,
        priceList,
        runPreferenceMutation
    ]);

    const handleSwitchToBan = useCallback(() => {
        setOptimistic({favorite: false, banned: true});
        void runPreferenceMutation(async () => {
            const favId = await findPreferenceIdByPriceList('favorite', priceList!);
            if (favId) {
                await removePreference(favId);
            }
            await addPreference({kind: 'ban', value: priceList!, comment: ''});
        });
    }, [
        addPreference,
        findPreferenceIdByPriceList,
        removePreference,
        priceList,
        runPreferenceMutation
    ]);

    const handleFavoriteClick: React.MouseEventHandler = (event) => {
        event.stopPropagation();
        if (!canUsePreferences) {
            return;
        }

        if (isFavorite) {
            handleRemoveFavorite();
        } else if (isBanned) {
            handleSwitchToFavorite();
        } else {
            handleAddFavorite();
        }
    };

    const handleBanClick: React.MouseEventHandler = (event) => {
        event.stopPropagation();
        if (!canUsePreferences) {
            return;
        }

        if (isBanned) {
            handleRemoveBan();
        } else if (isFavorite) {
            handleSwitchToBan();
        } else {
            handleAddBan();
        }
    };

    const applyRatingAction = (kind: 'favorite' | 'ban') => {
        if (!canUsePreferences) {
            return;
        }

        if (kind === 'favorite') {
            if (isFavorite) {
                handleRemoveFavorite();
            } else if (isBanned) {
                handleSwitchToFavorite();
            } else {
                handleAddFavorite();
            }
            return;
        }

        if (isBanned) {
            handleRemoveBan();
        } else if (isFavorite) {
            handleSwitchToBan();
        } else {
            handleAddBan();
        }
    };

    const {highlightedIndex} = useMenuKeyboard({
        isOpen: isOpen && priceListActionsEnabled && Boolean(login),
        items: ['favorite', 'ban'] as const,
        onSelect: (kind) => applyRatingAction(kind),
        onClose: () => setIsOpen(false)
    });

    const handleIconKeyDown = (
        event: React.KeyboardEvent<SVGElement>,
        kind: 'favorite' | 'ban'
    ) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (kind === 'favorite') {
            applyRatingAction('favorite');
        } else {
            applyRatingAction('ban');
        }
    };

    // const showPreferenceIcon = enablePriceListActions && (isFavorite || isBanned);
    const showPreferenceIcon = priceListActionsEnabled && (isFavorite || isBanned);

    return (
        <div ref={rootRef} className={b(null, className)}>
            <div
                className={b('progress-trigger', {disabled: isPopoverDisabled})}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                role={isPopoverDisabled ? undefined : 'button'}
                tabIndex={isPopoverDisabled ? -1 : 0}
            >
                <Progress
                    className={b('progress')}
                    size="m"
                    value={progressValue}
                    text={progressText.replace(/\s+%/g, '%')}
                    colorStops={[
                        {theme: 'danger', stop: 44},
                        {theme: 'warning', stop: 84},
                        {theme: 'success', stop: 100}
                    ]}
                />

                {showPreferenceIcon &&
                    (isBanned ? (
                        <Ban className={b('ban-icon')} aria-hidden />
                    ) : (
                        <StarFill className={b('star-icon')} aria-hidden />
                    ))}
            </div>

            {!isPopoverDisabled && (
                <div className={b('tooltip-content', {open: isOpen})} aria-hidden={!isOpen}>
                    <Text variant="caption-2">{priceList}</Text>

                    {priceListActionsEnabled && (
                        <>
                            {isFavorite ? (
                                <StarFill
                                    className={b('star-icon', {
                                        interactive: true,
                                        pending: actionPending,
                                        highlighted: highlightedIndex === 0
                                    })}
                                    aria-label="Удалить из избранных прайсов"
                                    role="button"
                                    tabIndex={login ? 0 : -1}
                                    onClick={handleFavoriteClick}
                                    onKeyDown={(e) => handleIconKeyDown(e, 'favorite')}
                                />
                            ) : (
                                <Star
                                    className={b('tooltip-icon', {
                                        disabled: !login,
                                        pending: actionPending,
                                        highlighted: highlightedIndex === 0
                                    })}
                                    aria-label={
                                        isBanned
                                            ? 'Заменить бан на избранное'
                                            : 'Добавить в избранные прайсы'
                                    }
                                    role="button"
                                    tabIndex={login ? 0 : -1}
                                    onClick={handleFavoriteClick}
                                    onKeyDown={(e) => handleIconKeyDown(e, 'favorite')}
                                />
                            )}
                            {isBanned ? (
                                <Ban
                                    className={b('ban-icon', {
                                        interactive: true,
                                        pending: actionPending,
                                        highlighted: highlightedIndex === 1
                                    })}
                                    aria-label="Удалить из бан-прайсов"
                                    role="button"
                                    tabIndex={login ? 0 : -1}
                                    onClick={handleBanClick}
                                    onKeyDown={(e) => handleIconKeyDown(e, 'ban')}
                                />
                            ) : (
                                <Ban
                                    className={b('tooltip-icon', {
                                        disabled: !login,
                                        pending: actionPending,
                                        highlighted: highlightedIndex === 1
                                    })}
                                    aria-label={
                                        isFavorite
                                            ? 'Заменить избранное на бан'
                                            : 'Добавить в бан-прайсы'
                                    }
                                    role="button"
                                    tabIndex={login ? 0 : -1}
                                    onClick={handleBanClick}
                                    onKeyDown={(e) => handleIconKeyDown(e, 'ban')}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
